/** @format */
import { Trans, useTranslation } from 'next-i18next';
import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';

import { trackViaDataLayer } from '@toucan/client/shared/helpers/analytics';

import {
    DEFAULT_TEST_ID,
    KIT_INTELLIGENCE_FEEDBACK,
    KitFeedbackVerdict,
} from './config/constants';
import { KitFeedbackProps } from './config/types';
import { styles } from './kit-feedback.styles';

export * from './config/constants';
export type { KitFeedbackProps } from './config/types';

const StyledKitFeedback = styled.div`
    ${styles}
`;

const FeedbackTick: FunctionComponent = () => (
    <svg
        className="kit-feedback-tick"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
    >
        <path
            className="kit-feedback-tick-mark"
            d="M5 13 L9.5 17.5 L19 7"
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Asks whether the grid above it was any use, and reports the answer.
 *
 * The answer is held in memory only. It is a signal for the reports rather
 * than a preference worth remembering, so a reader who reloads the page is
 * asked again — and nothing has to be stored about them to make that work.
 */
export const KitFeedback: FunctionComponent<KitFeedbackProps> = ({
    modelId,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    ...otherProps
}) => {
    const { t } = useTranslation();
    const [verdict, setVerdict] = useState<KitFeedbackVerdict | null>(null);
    const hasAnswered = verdict !== null;

    /**
     * Both buttons stay live after an answer, so a reader who decides they
     * were wrong can say so. Pressing the answer they already gave is not a
     * change of mind and reports nothing; pressing the other one reports
     * the new verdict, and the reports read the last one for a given reader
     * as what they settled on.
     */
    const answer = (given: KitFeedbackVerdict) => {
        if (given === verdict) {
            return;
        }

        setVerdict(given);

        trackViaDataLayer({
            event: KIT_INTELLIGENCE_FEEDBACK,
            verdict: given,
            model_id: modelId,
        });
    };

    const handleUsefulClick = () => answer(KitFeedbackVerdict.USEFUL);
    const handleNotUsefulClick = () => answer(KitFeedbackVerdict.NOT_USEFUL);

    return (
        <StyledKitFeedback data-testid={dataTestId} {...otherProps}>
            {hasAnswered ? (
                <p
                    className="kit-feedback-question"
                    role="status"
                    data-testid={`${dataTestId}__thanks`}
                >
                    {t(
                        'Thanks for the feedback, it helps us work out what to keep'
                    )}
                </p>
            ) : (
                <p className="kit-feedback-question">
                    <Trans
                        components={{
                            'beta-text': (
                                <strong className="kit-feedback-beta" />
                            ),
                        }}
                    >
                        {t(
                            'This is a <beta-text>beta</beta-text> feature - was this useful?'
                        )}
                    </Trans>
                </p>
            )}

            <div className="kit-feedback-actions">
                <button
                    type="button"
                    className="kit-feedback-button"
                    aria-pressed={verdict === KitFeedbackVerdict.USEFUL}
                    onClick={handleUsefulClick}
                    data-testid={`${dataTestId}__useful`}
                >
                    <span className="kit-feedback-button-label">
                        {t('Useful')}
                    </span>
                    <FeedbackTick />
                </button>
                <button
                    type="button"
                    className="kit-feedback-button"
                    aria-pressed={verdict === KitFeedbackVerdict.NOT_USEFUL}
                    onClick={handleNotUsefulClick}
                    data-testid={`${dataTestId}__not-useful`}
                >
                    <span className="kit-feedback-button-label">
                        {t('Not useful')}
                    </span>
                    <FeedbackTick />
                </button>
            </div>

            <p
                className="kit-feedback-disclaimer"
                data-testid={`${dataTestId}__disclaimer`}
            >
                {t(
                    "This feature is in beta. We're still testing it, and some content is generated by AI. AI-generated content may be inaccurate, so check important information before relying on it."
                )}
            </p>
        </StyledKitFeedback>
    );
};
